import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import PagedList from "../../common/PagedList";
import {Checkbox, Select, Input} from "antd";
import IconButton from "../../common/IconButton";
import {DeleteIcon} from "../../common/icons";
import Alert from "../../common/Alert";
import {CheckCircleOutlined, LoadingOutlined, WarningOutlined} from "@ant-design/icons";
import {Clazz, ERRORS, REMOVE_TYPES, User} from "./BulkClassUpdate";
import GenericDialog from "../../common/GenericDialog";
import {EditableRow, GenEditableCell} from "../../tests/teacher/Diagnostics/TestGroups/EditableTable";
import axios from "../../common/axios";
import Tooltip from "../../common/Tooltip";

export interface ConfigureUsersRef {
    checkNextStep: () => void;
}

interface Props {
    users: Record<number, User>,
    newClasses: Record<string, Clazz>,
    schoolClasses: object,
    updateUser: (userId: number, userDetails: object) => void,
    updateUserClasses: (userId: number, classes: string[]) => void,
    removeUser: (userId: number) => void,
    reviewData: () => void
}

const ConfigureUsers = forwardRef<ConfigureUsersRef, Props>((props, ref) => {
    const {users, newClasses, schoolClasses, updateUser, updateUserClasses, removeUser, reviewData} = props;
    const [tableData, setTableData] = useState<object[]>([]);
    const [classes, setClasses] = useState<object>({});
    const [errors, setErrors] = useState<{userNotFound: number; noClasses: number}>({userNotFound: 0, noClasses: 0});
    const [filterByErrors, setFilterByErrors] = useState<boolean>(false);
    const [dialogSettings, setDialogSettings] = useState<object>({open: false});
    const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());

    const defaultColumns: object[] = [
        {
            dataIndex: 'icon',
            key: 'icon',
            width: 30
        },
        {
            title: 'Email',
            dataIndex: 'username',
            key: 'username',
            editable: true
        },
        {
            title: 'First Name',
            dataIndex: 'firstName',
            key: 'firstName',
            width: 100
        },
        {
            title: 'Last Name',
            dataIndex: 'lastName',
            key: 'lastName',
            width: 100
        },
        {
            title: 'New Classes',
            dataIndex: 'classes',
            key: 'classes',
            width: '45%'
        }
    ]

    const handleUpdateUser = (userId: number, userDetails: object): void => {
        updateUser(userId, userDetails);
    }

    const handleUpdateUserClasses = (userId: number, classes: string[]): void => {
        updateUserClasses(userId, classes);
    }

    const handleRemoveUser = (userId: number): void => {
        setDialogSettings({
            open: true,
            title: "Remove User?",
            onClose: () => {setDialogSettings({...dialogSettings, open: false})},
            buttons: [
                {
                    name: 'Cancel',
                    extraProps: {type: 'text'}

                }, {
                    name: 'Delete',
                    callback: async () => {
                        removeUser(userId);
                        setDialogSettings({...dialogSettings, open: false});
                    }
                }
            ],
            children: <p>Remove user from update list?</p>
        });
    }

    const checkNextStep = (): void => {
        reviewData();
    }

    useEffect(() => {
        if (users != null && classes != null) {
            let newTableData: object[] = [];
            let errors: {userNotFound: number; noClasses: number} = {
                userNotFound: 0,
                noClasses: 0
            };
            Object.entries(users).map(([key, user]): void => {
                if (user.removed !== REMOVE_TYPES.REMOVED) {
                    if (user.error === ERRORS.USER_NOT_FOUND) errors.userNotFound++;
                    if (user.error === ERRORS.NO_CLASSES) errors.noClasses++;
                    const isLoading = loadingRows.has(Number(user.sequence));
                    const newUserData: object = {
                        key: key,
                        icon: isLoading ? (
                            <Tooltip title="Saving...">
                                <LoadingOutlined style={{ fontSize: '20px'}} spin />
                            </Tooltip>
                        ) : (
                            (user.error !== ERRORS.NULL && user.error !== ERRORS.REMOVING) ? (
                                <Tooltip title={user.error.tooltip}>
                                    <WarningOutlined style={{ fontSize: '20px' }} />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Ready for import">
                                    <CheckCircleOutlined style={{ fontSize: '20px', color: "var(--success-colour)" }} />
                                </Tooltip>
                            )
                        ),
                        username: user.username,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        sequence: user.sequence,
                        classes:
                            <div className="flexSpaceBetween flexAlignCentre">
                                {user.error === ERRORS.USER_NOT_FOUND ? (
                                    <div className="flex"><strong>User Not Found</strong></div>
                                ) : (
                                    <Select
                                        style={{flex: "1"}}
                                        mode="multiple"
                                        placeholder="Select a class"
                                        value={user.classes || []}
                                        status={user.classes.length <= 0 ? "error" : ""}
                                        options={
                                            Object.entries(classes)
                                                .sort(([, a], [, b]) => a.className.localeCompare(b.className))
                                                .map(([key, clazz]) => ({
                                                    value: (clazz && clazz.classId ? clazz.classId : clazz.className),
                                                    label: clazz.className,
                                                }))
                                        }
                                        onChange={(classes) => handleUpdateUserClasses(user.userId, classes)}
                                    />
                                )}
                                <div className="flex margin-l--m">
                                    <IconButton tooltip="Remove user">
                                        <DeleteIcon onClick={() => handleRemoveUser(user.userId)}/>
                                    </IconButton>
                                </div>
                            </div>,
                        rowClass: "" + (user.error === ERRORS.NULL ? "" : user.error === ERRORS.REMOVING ? " ch-alert-success-fade" : "ant-alert-warning ch-alert-warning")
                    }
                    if (!filterByErrors || user.error !== ERRORS.NULL) {
                        newTableData.push(newUserData);
                    }
                }
            });
            newTableData.sort((a, b) => a.sequence - b.sequence);
            setTableData(newTableData);
            setErrors(errors);
        }
    }, [users, classes, filterByErrors, loadingRows]);

    useEffect(() => {
        if (newClasses != null && schoolClasses != null) {
            setClasses({...newClasses, ...schoolClasses});
        }
    }, [newClasses, schoolClasses]);

    useImperativeHandle(ref, () => ({
        checkNextStep
    }))

    const columns = defaultColumns.map((col) => {
        if (!col.editable) {
            return col;
        }
        return {
            ...col,
            onCell: (record) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave,
            }),
        };
    });

    const handleSave = (row, value) => {
        const username = value != null ? value : row.username;
        setLoadingRows((prev) => new Set(prev).add(row.sequence));
        axios.get('/web/ui/sc/searchUser',{params: {username: username}})
            .then(response => {
                let newUserDetails: object = {
                    userId: row.key,
                    firstName: '',
                    lastName: '',
                    username: username,
                    matchedUser: false,
                    currentClasses: [],
                    subscriberType: ''
                };
                if (response.data.user) {
                    newUserDetails = {
                        userId: response.data.user.userId,
                        firstName: response.data.user.firstName,
                        lastName: response.data.user.lastName,
                        username: response.data.user.username,
                        matchedUser: true,
                        currentClasses: response.data.user.classIds,
                        subscriberType: response.data.user.subscriberType
                    }
                }
                handleUpdateUser(row.key, newUserDetails);
            })
            .finally(() => {
                setLoadingRows((prev) => {
                    const updatedSet = new Set(prev);
                    updatedSet.delete(row.sequence);
                    return updatedSet;
                });
            });
    }

    return (
        <>
            <h1>Manage User Import</h1>
            <p>
                <span>Ensure that users are being added to the correct classes. Users who don't have a valid subscription will not be added. Add subscriptions for new users in Cambridge GO.</span>
            </p>
            {(errors.noClasses + errors.userNotFound) > 0 && (
                <Alert
                    className="margin-t--l"
                    type="warning"
                    showIcon
                    description={
                        <>
                            {errors.noClasses > 0 && (
                                <p>There {errors.noClasses > 1 ? "are" : "is"} <strong>{errors.noClasses}</strong> {errors.noClasses > 1 ? "users" : "user"} without an assigned class. Assign classes using the New Classes field.</p>
                            )}
                            {errors.userNotFound > 0 && (
                                <p>There {errors.userNotFound > 1 ? "are" : "is"} <strong>{errors.userNotFound}</strong> {errors.userNotFound > 1 ? "users" : "user"} where a matching user account is not found. Users must already exist and be assigned to your school for changes to take effect.</p>
                            )}
                        </>
                    }
                />
            )}
            <Checkbox className="margin-t--l" checked={filterByErrors}
                      onChange={() => setFilterByErrors(!filterByErrors)}>
                Show rows with problems only
            </Checkbox>
            <PagedList
                className="margin-t--l"
                columns={columns}
                dataSource={tableData}
                rowClassName={(record) => record.rowClass}
                components={{
                    body: {
                        row: EditableRow,
                        cell: (props) => (
                            <GenEditableCell
                                input={
                                    <Input.Search enterButton onSearch={(value) => handleSave(props.record, value)} />
                                }
                                {...props}>
                                {props.children}
                            </GenEditableCell>
                        ),
                    },
                }}
            />
            <GenericDialog {...dialogSettings}></GenericDialog>
        </>
    )
});

export default ConfigureUsers;